import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { AvailableOnSide, Finish } from "@tosize/shapes/src/types/finish";
import { cn } from "@tosize/ui";
import { Accordion } from "radix-ui";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePostDataContext } from "../../../../context/PostDataContext";
import useFinishSide from "../../../../hooks/useFinishSide";
import useSelectedFinishes from "../../../../hooks/useSelectedFinishes";
import { ActiveDrillHole, DrillHoles } from "../DrillHoles";
import FinishWarning from "../FinishWarning";
import { getWorkPieceDrillholes } from "../utils";

interface FinishOptionDrillHolesProps {
	finish: Finish;
	disabled: boolean;
}

export function FinishOptionDrillHoles({ finish, disabled }: FinishOptionDrillHolesProps) {
	const { setCanvasSettings } = usePostDataContext();

	return (
		<Accordion.Root
			type="single"
			collapsible
			className="flex flex-col gap-4"
			onValueChange={(value) => setCanvasSettings({ view: "detail", field: value })}
		>
			{finish.availableOnSides
				.sort((a, b) => a.sideOrder - b.sideOrder)
				.map((side) => (
					<FinishOptionDrillHolesItem
						finish={finish}
						side={side}
						disabled={disabled}
						key={side.sideCode}
					/>
				))}
		</Accordion.Root>
	);
}

interface FinishOptionDrillHolesItemProps extends FinishOptionDrillHolesProps {
	side: AvailableOnSide;
}

function FinishOptionDrillHolesItem({ side, finish, disabled }: FinishOptionDrillHolesItemProps) {
	const intl = useIntl();
	const { workpiece, setCanvasSettings } = usePostDataContext();
	const workPieceHoles = getWorkPieceDrillholes({ workpiece, side });
	const { getSelectedFinishesOnSide, removeFinishSides } = useSelectedFinishes();
	const finishes = getSelectedFinishesOnSide(side);
	const { appliedFinish, sideLengthString } = useFinishSide({ side, intl });
	const isAlreadyChecked = finishes.length > 0 && !finishes.includes(side.finishCode);
	const [focus, setFocused] = useState(false);

	return (
		<Accordion.Item
			value={side.sideCode}
			className={cn("group rounded border border-shades motion-safe:transition", {
				"cursor-not-allowed bg-shades-light": disabled,
				"hover:border-primary hover:rdx-state-open:border-primary-medium": !disabled,
				"rdx-state-open:ring-primary-medium ring-1 ring-primary": focus,
			})}
			onMouseOver={(e) => {
				const state =
					e.currentTarget instanceof HTMLElement
						? e.currentTarget.getAttribute("data-state")
						: undefined;

				return setCanvasSettings(() => ({
					view: state === "open" ? "detail" : "2d",
					field: side.sideCode,
				}));
			}}
			onMouseLeave={() => setCanvasSettings((prev) => (prev?.view === "detail" ? prev : undefined))}
		>
			<Accordion.Trigger
				disabled={disabled}
				className="flex w-full items-center justify-between gap-4 p-4 outline-none"
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				onClick={() => {
					if (isAlreadyChecked) {
						removeFinishSides([side]);
					}
				}}
			>
				<div className="flex items-baseline justify-start gap-1 text-left font-bold">
					<div>{side.sideName}</div>

					{!!sideLengthString && <div className="text-shades-dark text-xs">{sideLengthString}</div>}
				</div>

				<div className="group-rdx-state-open:rotate-180">
					<FontAwesomeIcon icon={faChevronDown} fixedWidth />
				</div>
			</Accordion.Trigger>

			{isAlreadyChecked && (
				<FinishWarning className="px-4 pb-4">
					<FormattedMessage
						defaultMessage="Er is al {finish} op deze zijde toegepast"
						id="eqNyKy"
						values={{
							finish: appliedFinish?.label.toLocaleLowerCase(),
						}}
					/>
				</FinishWarning>
			)}

			{finish.mandatory && (
				<FinishWarning className="px-4 pb-4">
					<FormattedMessage defaultMessage="Deze afwerking is verplicht" id="C2FG5S" />
				</FinishWarning>
			)}

			{!!workPieceHoles.length && (
				<div className="flex w-full flex-col gap-4 px-4 pb-4 group-rdx-state-open:hidden">
					{workPieceHoles.map((hole) => (
						<ActiveDrillHole hole={hole} key={hole.position} />
					))}
				</div>
			)}

			<Accordion.Content className="flex-col gap-4 px-4 pb-4 group-rdx-state-open:flex">
				<DrillHoles finish={finish} side={side} />
			</Accordion.Content>
		</Accordion.Item>
	);
}
